/**
 * "Notify me when available" (19 Sep 2026) for Ape the Label.
 * Markup: snippets/notify-me.liquid   Styles: assets/notify-me.css
 *
 * Product pages and the Quick Buy pop-up: when the chosen size is sold out, the theme's own Add to Cart
 * button becomes "Notify me when available". Tapping it opens a short sign-up that registers the shopper
 * for Klaviyo's back-in-stock email for that exact size. It sends the same request as Klaviyo's own
 * back-in-stock form, so the existing Klaviyo back-in-stock flow sends the emails.
 *
 * The button follows the theme's own sold-out state (the form's "variant--soldout" class), so it is never
 * out of step with the selected size. Klaviyo's auto-inserted button (product pages only, and it could miss
 * a size change) is hidden in favour of this one; the floating Add to Cart bar uses this button too.
 *
 * Also keeps the Add to Cart price steady: the page loads with "$119.99" in the button, but the theme
 * rewrites it as "$119.99 AUD" after every size or quantity change. The short form is kept everywhere
 * (the full price with the currency code sits just above the button).
 */
(() => {
  if (window.apeNotifyMe) return;
  window.apeNotifyMe = true;

  const WRAPPER = '[data-form-wrapper]';
  const BUTTON = '[data-add-to-cart]:not([data-upsell-btn])';
  const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

  const dialog = document.getElementById('ApeNotify');
  if (!dialog || typeof dialog.showModal !== 'function') return;

  const settings = dialog.dataset;
  const LABEL = settings.buttonLabel || 'Notify me when available';
  const company = () => {
    if (settings.klaviyoCompany) return settings.klaviyoCompany;
    const script = document.querySelector('script[src*="klaviyo.js?company_id="]');
    const match = script && script.src.match(/company_id=([^&]+)/);
    return match ? match[1] : '';
  };
  const part = (selector) => dialog.querySelector(selector);
  const ui = {
    form: part('[data-notify-form]'),
    email: part('[data-notify-email]'),
    error: part('[data-notify-error]'),
    submit: part('[data-notify-submit]'),
    copy: part('[data-notify-copy]'),
    success: part('[data-notify-success]'),
    successCopy: part('[data-notify-success-copy]'),
    title: part('[data-notify-title]'),
    variant: part('[data-notify-variant]'),
    media: part('[data-notify-media]'),
    image: part('[data-notify-image]'),
  };
  let current = null;

  /* ---------- 1. Add to Cart price stays in its short form ("$119.99", not "$119.99 AUD") ---------- */

  const currencyCode = () => {
    const active = window.Shopify && window.Shopify.currency && window.Shopify.currency.active;
    if (active) return active;
    const match = ((window.theme && window.theme.moneyWithCurrencyFormat) || '').match(/\s([A-Z]{3})\s*$/);
    return match ? match[1] : '';
  };

  const shortPrice = (button) => {
    const price = button.querySelector('[data-add-to-cart-text] [data-product-price]');
    const code = currencyCode();
    if (!price || !/^[A-Z]{3}$/.test(code)) return;
    const text = price.textContent;
    const short = text.replace(new RegExp(`\\s*${code}\\s*$`), '');
    if (short !== text) price.textContent = short;
  };

  /* ---------- 2. Sold-out Add to Cart buttons become "Notify me when available" ---------- */

  const update = (wrapper) => {
    const button = wrapper.querySelector(BUTTON);
    if (!button) return;
    shortPrice(button);
    // Klaviyo's back-in-stock script hides Add to Cart with an inline style for sold-out sizes; undo it.
    if (button.style.getPropertyValue('display') === 'none') button.style.removeProperty('display');

    if (wrapper.classList.contains('variant--soldout')) {
      if (!button.hasAttribute('data-notify-me')) button.setAttribute('data-notify-me', '');
      if (!button.classList.contains('is-notify')) button.classList.add('is-notify');
      if (button.getAttribute('aria-haspopup') !== 'dialog') button.setAttribute('aria-haspopup', 'dialog');
      if (button.disabled) button.disabled = false;
      const text = button.querySelector('[data-add-to-cart-text]');
      if (text && text.textContent.trim() !== LABEL) text.textContent = LABEL;
    } else if (button.hasAttribute('data-notify-me')) {
      // Back to an in-stock size: the theme writes "Add to Cart" and the price itself.
      button.removeAttribute('data-notify-me');
      button.removeAttribute('aria-haspopup');
      button.classList.remove('is-notify');
    }
  };

  const watched = new WeakSet();
  const watch = (wrapper) => {
    if (watched.has(wrapper)) return;
    watched.add(wrapper);
    update(wrapper);
    new MutationObserver(() => update(wrapper)).observe(wrapper, {
      attributes: true,
      attributeFilter: ['class', 'style', 'disabled'],
      childList: true,
      characterData: true,
      subtree: true,
    });
  };
  const scan = (node) => {
    if (node.matches && node.matches(WRAPPER)) watch(node);
    if (node.querySelectorAll) node.querySelectorAll(WRAPPER).forEach(watch);
  };
  scan(document);
  // Quick Buy pop-ups are added to the page when opened.
  new MutationObserver((records) => {
    records.forEach((record) => record.addedNodes.forEach((node) => node.nodeType === 1 && scan(node)));
  }).observe(document.body, { childList: true, subtree: true });

  // Tapping a Notify me button opens the sign-up instead of trying to add a sold-out size to the cart.
  document.addEventListener(
    'click',
    (event) => {
      const button = event.target instanceof Element ? event.target.closest('[data-notify-me]') : null;
      if (!button) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      open(button);
    },
    true
  );

  /* ---------- 3. The sign-up ---------- */

  const selectedOptions = (product) => {
    const selects = product.querySelector('variant-selects');
    if (!selects) return [];
    // One .selector-wrapper per option (e.g. Size): its label, and the value that is selected.
    return Array.from(selects.querySelectorAll('.selector-wrapper'))
      .map((group) => {
        const label = group.querySelector('.radio__legend__label, legend, label');
        const radio = group.querySelector('input[type="radio"]:checked');
        const dropdown = group.querySelector('select');
        const value = radio ? radio.value : dropdown ? dropdown.value : '';
        return value ? { name: label ? label.textContent.trim() : '', value } : null;
      })
      .filter(Boolean);
  };

  const open = (button) => {
    const wrapper = button.closest(WRAPPER);
    const product = button.closest('product-info') || wrapper || document;
    const form = button.form || (wrapper && wrapper.querySelector('form'));
    const idInput = form && form.querySelector('[name="id"]');
    if (!idInput || !idInput.value) return;

    const heading = product.querySelector('.product__title, .popup-quick-view__title');
    const title = heading ? heading.textContent.trim() : document.title;
    const options = selectedOptions(product);
    const sizeLabel = options.map((o) => (o.name ? `${o.name}: ${o.value}` : o.value)).join(' · ');
    const sizeValue = options.map((o) => o.value).join(' / ');
    const img = product.querySelector('.popup-quick-view__gallery img, .product__media img, img');

    current = { variantId: idInput.value, title, sizeValue, trigger: button };
    ui.title.textContent = title;
    ui.variant.textContent = sizeLabel;
    ui.variant.hidden = !sizeLabel;
    ui.copy.textContent = sizeLabel ? settings.copySize : settings.copyItem;
    if (img && (img.currentSrc || img.src)) {
      ui.image.src = img.currentSrc || img.src;
      ui.media.hidden = false;
    } else {
      ui.media.hidden = true;
    }
    ui.form.hidden = false;
    ui.success.hidden = true;
    hideError();

    dialog.showModal();
    document.documentElement.classList.add('ape-notify-open');
    if (window.matchMedia('(pointer: fine)').matches) ui.email.focus();
  };

  const close = () => {
    if (dialog.open) dialog.close();
  };
  dialog.addEventListener('close', () => document.documentElement.classList.remove('ape-notify-open'));
  dialog.addEventListener('click', (event) => {
    if (event.target === dialog || event.target.closest('[data-notify-close]')) close();
  });

  const showError = (message) => {
    ui.error.textContent = message;
    ui.error.hidden = false;
    ui.email.setAttribute('aria-invalid', 'true');
  };
  const hideError = () => {
    ui.error.hidden = true;
    ui.email.removeAttribute('aria-invalid');
  };
  ui.email.addEventListener('input', hideError);

  const busy = (on) => {
    ui.submit.disabled = on;
    ui.submit.setAttribute('aria-busy', String(on));
    ui.form.classList.toggle('is-busy', on);
  };

  // Same request as Klaviyo's own back-in-stock form.
  const subscribe = (email, variantId) =>
    fetch(`https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${encodeURIComponent(company())}&revamp=true`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', revision: '2025-04-15' },
      body: JSON.stringify({
        data: {
          type: 'back-in-stock-subscription',
          attributes: {
            channels: ['EMAIL'],
            profile: { data: { type: 'profile', attributes: { email } } },
          },
          relationships: {
            variant: { data: { type: 'catalog-variant', id: `$shopify:::$default:::${variantId}` } },
          },
        },
      }),
    });

  ui.form.addEventListener('submit', async (event) => {
    event.preventDefault();
    if (!current) return;
    const email = ui.email.value.trim();
    if (!EMAIL_PATTERN.test(email)) {
      showError(settings.errorEmail);
      ui.email.focus();
      return;
    }
    hideError();
    busy(true);
    try {
      const response = await subscribe(email, current.variantId);
      if (!response.ok) throw new Error(`Klaviyo ${response.status}`);
      try {
        if (window.klaviyo && typeof window.klaviyo.identify === 'function') window.klaviyo.identify({ email });
      } catch (error) {
        /* identifying the browser is a nice-to-have */
      }
      const what = current.sizeValue ? `${current.title} in ${current.sizeValue}` : current.title;
      ui.successCopy.textContent = `We'll email ${email} as soon as ${what} is back.`;
      ui.form.hidden = true;
      ui.success.hidden = false;
      const done = ui.success.querySelector('[data-notify-close]');
      if (done) done.focus();
    } catch (error) {
      showError(settings.errorGeneric);
    } finally {
      busy(false);
    }
  });
})();
